import { useState, useEffect, useRef, useCallback } from 'react';

interface LocalServiceUpdateState {
  hasPendingUpdate: boolean;
  version: string;
  dismissed: boolean;
}

export function useLocalServiceUpdateCheck(localServiceUrl: string | null | undefined, checkIntervalMs = 60000) {
  const [updateState, setUpdateState] = useState<LocalServiceUpdateState>({
    hasPendingUpdate: false,
    version: '',
    dismissed: false,
  });
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkForUpdate = useCallback(async () => {
    if (!localServiceUrl) return;
    try {
      const resp = await fetch(`${localServiceUrl}/api/pending-update`, {
        signal: AbortSignal.timeout(5000),
      });
      const data = await resp.json();
      if (data.hasPendingUpdate) {
        setUpdateState(prev => ({
          ...prev,
          hasPendingUpdate: true,
          version: data.version || '',
        }));
      } else {
        setUpdateState(prev => ({ ...prev, hasPendingUpdate: false, version: '' }));
      }
    } catch {
      // local service unreachable - ignore
    }
  }, [localServiceUrl]);

  useEffect(() => {
    if (!localServiceUrl) return;

    const initialDelay = setTimeout(() => {
      checkForUpdate();
      timerRef.current = setInterval(checkForUpdate, checkIntervalMs);
    }, 10000);

    return () => {
      clearTimeout(initialDelay);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [localServiceUrl, checkIntervalMs, checkForUpdate]);

  const applyUpdateAndRestart = useCallback(async () => {
    if (!localServiceUrl) return false;
    try {
      const resp = await fetch(`${localServiceUrl}/api/apply-update-and-restart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(10000),
      });
      const data = await resp.json();
      return data.success === true;
    } catch {
      return false;
    }
  }, [localServiceUrl]);

  const dismiss = useCallback(() => {
    setUpdateState(prev => ({ ...prev, dismissed: true }));
  }, []);

  return {
    hasPendingUpdate: updateState.hasPendingUpdate,
    version: updateState.version,
    dismissed: updateState.dismissed,
    applyUpdateAndRestart,
    dismiss,
  };
}
